import React, { PropsWithChildren, useMemo } from 'react';
import { Box, Button, Checkbox, Flex, Text } from '@fullstory/components';
import css from './index.module.scss';
import { BulkActionResourceType } from '../types';
import { intl } from '@fullstory/intl';

interface BulkActionsProps {
  resourceType: BulkActionResourceType;
  numRowsOnPage: number;
  numSelected: number;
  totalRows: number;
  toggleAllDisabled: boolean;
  onTogglePage: () => void;
  allSelected: boolean;
  onSelectAll: () => void;
  testId: string;
  simplified?: boolean;
  disableSelectAll?: boolean;
}

const bulkActionsMessages: {
  [key in BulkActionResourceType]: {
    selectedLabel: (numSelected: number) => string;
    totalLabel: (numTotal: number) => string;
    selectAllLabel: (numTotal: number) => string;
  };
} = {
  Property: {
    selectedLabel: (numSelected: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            '{numSelected} {numSelected, plural, one {Property} other {Properties}} selected',
          id: 'cxCzsK',
        },
        {
          numSelected,
        },
      ),
    totalLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage: '{numTotal} {numTotal, plural, one {Property} other {Properties}}',
          id: 'KLjqm/',
        },
        {
          numTotal,
        },
      ),
    selectAllLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            'Select all {numTotal} {numTotal, plural, one {Property} other {Properties}}',
          id: 'npz9Bb',
        },
        {
          numTotal,
        },
      ),
  },
  Page: {
    selectedLabel: (numSelected: number) =>
      intl.formatMessage(
        {
          defaultMessage: '{numSelected} {numSelected, plural, one {Page} other {Pages}} selected',
          id: 'E/veFU',
        },
        {
          numSelected,
        },
      ),
    totalLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage: '{numTotal} {numTotal, plural, one {Page} other {Pages}}',
          id: 'aHRFbv',
        },
        {
          numTotal,
        },
      ),
    selectAllLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage: 'Select all {numTotal} {numTotal, plural, one {Page} other {Pages}}',
          id: 'btWU5d',
        },
        {
          numTotal,
        },
      ),
  },
  Element: {
    selectedLabel: (numSelected: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            '{numSelected} {numSelected, plural, one {Element} other {Elements}} selected',
          id: 'CIUZO3',
        },
        {
          numSelected,
        },
      ),
    totalLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage: '{numTotal} {numTotal, plural, one {Element} other {Elements}}',
          id: 'KGGJdY',
        },
        {
          numTotal,
        },
      ),
    selectAllLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            'Select all {numTotal} {numTotal, plural, one {Element} other {Elements}}',
          id: 'MJLFrl',
        },
        {
          numTotal,
        },
      ),
  },
  ElementPropertyRule: {
    selectedLabel: (numSelected: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            '{numSelected} {numSelected, plural, one {Property Rule} other {Property Rules}} selected',
          id: 'ZBoOSA',
        },
        {
          numSelected,
        },
      ),
    totalLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            '{numTotal} {numTotal, plural, one {Property Rule} other {Property Rules}}',
          id: 'DljMeO',
        },
        {
          numTotal,
        },
      ),
    selectAllLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            'Select all {numTotal} {numTotal, plural, one {Property Rule} other {Property Rules}}',
          id: 'rMF17p',
        },
        {
          numTotal,
        },
      ),
  },
  Event: {
    selectedLabel: (numSelected: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            '{numSelected} {numSelected, plural, one {Event} other {Events}} selected',
          id: 'bxDPZk',
        },
        {
          numSelected,
        },
      ),
    totalLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage: '{numTotal} {numTotal, plural, one {Event} other {Events}}',
          id: 'JnF3ub',
        },
        {
          numTotal,
        },
      ),
    selectAllLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage: 'Select all {numTotal} {numTotal, plural, one {Event} other {Events}}',
          id: 'yklWz9',
        },
        {
          numTotal,
        },
      ),
  },
  DxObject: {
    selectedLabel: (numSelected: number) =>
      intl.formatMessage(
        {
          defaultMessage: '{numSelected} {numSelected, plural, one {item} other {items}} selected',
          id: 'qBDQfq',
        },
        {
          numSelected,
        },
      ),
    totalLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage: '{numTotal} {numTotal, plural, one {item} other {items}}',
          id: 'u9cJAg',
        },
        {
          numTotal,
        },
      ),
    selectAllLabel: (numTotal: number) =>
      intl.formatMessage(
        {
          defaultMessage: 'Select all {numTotal} {numTotal, plural, one {item} other {items}}',
          id: 'mLJyDK',
        },
        {
          numTotal,
        },
      ),
  },
};

/**
 * Toolbar containing a checkbox to toggle all items
 * and an array of bulk action items (passed in as children).
 */
export const BulkActions: React.FC<PropsWithChildren<BulkActionsProps>> = ({
  resourceType,
  numRowsOnPage,
  numSelected,
  totalRows,
  toggleAllDisabled,
  onTogglePage,
  onSelectAll,
  allSelected,
  simplified,
  testId,
  children: bulkActions,
  disableSelectAll,
}) => {
  const messages = bulkActionsMessages[resourceType];
  const selectedText = useMemo(() => {
    if (allSelected) return messages.selectedLabel(totalRows);
    if (numSelected > 0) return messages.selectedLabel(numSelected);
    if (!simplified) return messages.totalLabel(totalRows);
    return null;
  }, [allSelected, messages, numSelected, simplified, totalRows]);

  return (
    <Flex
      data-component="BulkActions"
      className={css.bulkActions}
      justifyContent="flex-start"
      alignItems="center"
    >
      {/* TODO align this checkbox with the column not using hardcoded margin */}
      {!simplified && (
        <Box marginRight={24}>
          <Checkbox
            testId={`${testId}-checkbox-all`}
            targetSize="large"
            checked={numSelected === totalRows || allSelected}
            indeterminate={0 < numSelected && numSelected < totalRows && !allSelected}
            disabled={toggleAllDisabled}
            onChange={onTogglePage}
          />
        </Box>
      )}
      {selectedText && <Text appearance="bodySmall">{selectedText}</Text>}
      {!allSelected &&
        numSelected === numRowsOnPage &&
        numSelected < totalRows &&
        !disableSelectAll && (
          <Box marginLeft={12}>
            <Button onClick={onSelectAll} variant="fill-secondary">
              {messages.selectAllLabel(totalRows)}
            </Button>
          </Box>
        )}
      {bulkActions}
    </Flex>
  );
};
